/**
 * Budget Burn Monitor Page
 * AIDLC Administrative Backend
 *
 * Real-time budget health dashboard showing spend vs. budget,
 * burn rate, active alerts, and intervention history.
 * Consumes the backend /api/burn-monitor endpoints.
 *
 * Personas: Executive, Ops, Orchestrator
 */

import { useState, useEffect, useCallback } from 'react';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';
import { Button } from '../shared/Button';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import { fmtNum, fmtTokens, fmtDateTime } from '../../utils/formatters';
import type {
  BurnHealth,
  BurnHealthStatus,
  BurnAlert,
  AlertSeverity,
  AlertType,
  BurnIntervention,
  InterventionType,
} from '../../services/orchestrationService';
import {
  getBurnHealth,
  getBurnAlerts,
  acknowledgeBurnAlert,
  getInterventions,
} from '../../services/orchestrationService';

/** Health status badge config */
const healthConfig: Record<BurnHealthStatus, { label: string; variant: 'success' | 'warning' | 'danger' }> = {
  healthy: { label: 'Healthy', variant: 'success' },
  warning: { label: 'Warning', variant: 'warning' },
  critical: { label: 'Critical', variant: 'danger' },
};

/** Alert severity badge config */
const severityConfig: Record<AlertSeverity, { label: string; variant: 'info' | 'warning' | 'danger' }> = {
  info: { label: 'Info', variant: 'info' },
  warning: { label: 'Warning', variant: 'warning' },
  critical: { label: 'Critical', variant: 'danger' },
};

/** Alert type display labels */
const alertTypeLabels: Record<AlertType, string> = {
  over_burn: 'Over Burn',
  agent_blocked: 'Agent Blocked',
  budget_low: 'Budget Low',
  budget_exhausted: 'Budget Exhausted',
  burn_rate_high: 'High Burn Rate',
};

/** Intervention type display labels */
const interventionTypeLabels: Record<InterventionType, string> = {
  pause_agents: 'Pause Agents',
  reduce_scope: 'Reduce Scope',
  add_budget: 'Add Budget',
  reassign: 'Reassign',
  cancel: 'Cancel',
};

/** Get progress bar color class based on percentage */
function getProgressColor(pct: number): string {
  if (pct >= 85) return 'bg-red-500';
  if (pct >= 70) return 'bg-yellow-500';
  return 'bg-green-500';
}

/** Get remaining text color based on health status */
function getRemainingColor(status: BurnHealthStatus): string {
  if (status === 'healthy') return 'text-green-600';
  if (status === 'warning') return 'text-yellow-600';
  return 'text-red-600';
}

type AlertFilter = 'all' | 'unacknowledged';

// ── Main Component ───────────────────────────────────────────────────

export function BudgetBurnMonitorPage() {
  const [health, setHealth] = useState<BurnHealth | null>(null);
  const [alerts, setAlerts] = useState<BurnAlert[]>([]);
  const [interventions, setInterventions] = useState<BurnIntervention[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [alertFilter, setAlertFilter] = useState<AlertFilter>('all');
  const [showInterventions, setShowInterventions] = useState(false);
  const [acknowledgingId, setAcknowledgingId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [healthData, alertsData, interventionsData] = await Promise.all([
        getBurnHealth(),
        getBurnAlerts(),
        getInterventions(),
      ]);
      setHealth(healthData);
      setAlerts(alertsData);
      setInterventions(interventionsData);
    } catch (err) {
      console.error('[BurnMonitor] Failed to load:', err);
      setError('Failed to load burn monitor data. Please check the backend connection.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAcknowledge = async (alertId: string) => {
    setAcknowledgingId(alertId);
    try {
      await acknowledgeBurnAlert(alertId);
      setAlerts((prev) =>
        prev.map((a) =>
          a.id === alertId
            ? { ...a, acknowledged: true, acknowledgedAt: new Date().toISOString() }
            : a,
        ),
      );
    } catch (err) {
      console.error('[BurnMonitor] Failed to acknowledge alert:', err);
    } finally {
      setAcknowledgingId(null);
    }
  };

  const filteredAlerts =
    alertFilter === 'unacknowledged'
      ? alerts.filter((a) => !a.acknowledged)
      : alerts;

  const unacknowledgedCount = alerts.filter((a) => !a.acknowledged).length;

  if (isLoading) {
    return <LoadingSpinner message="Loading burn monitor data..." />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="text-red-500 mb-4">
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-gray-700 font-medium mb-2">Unable to Load Burn Monitor</p>
        <p className="text-sm text-gray-500 mb-4 max-w-md text-center">{error}</p>
        <button
          onClick={loadData}
          className="px-4 py-2 bg-ldaja-blue text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!health) {
    return (
      <EmptyState
        title="No budget data available"
        description="Budget health data is not yet available. Start a milestone to begin tracking."
      />
    );
  }

  const remaining = health.totalRemaining;
  const alertCount = health.unacknowledgedAlerts;
  const spentPct = health.totalBudget > 0
    ? Math.min((health.totalSpent / health.totalBudget) * 100, 100)
    : 0;

  return (
    <div>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-ldaja-black">Budget Burn Monitor</h1>
          <p className="text-sm text-gray-500 mt-1">
            Real-time budget health, spend tracking, and alert management.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={healthConfig[health.status]?.variant || 'neutral'} dot>
            {healthConfig[health.status]?.label || health.status}
          </Badge>
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Health Overview Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatCard
          label="Total Budget"
          value={fmtTokens(health.totalBudget)}
          subtext="tokens"
          colorClass="text-ldaja-blue"
          bgClass="bg-blue-50"
        />
        <StatCard
          label="Total Spent"
          value={fmtTokens(health.totalSpent)}
          subtext={`${spentPct.toFixed(1)}% used`}
          colorClass="text-gray-700"
          bgClass="bg-gray-50"
        />
        <StatCard
          label="Remaining"
          value={fmtTokens(remaining)}
          subtext="tokens left"
          colorClass={getRemainingColor(health.status)}
          bgClass={
            health.status === 'healthy'
              ? 'bg-green-50'
              : health.status === 'warning'
                ? 'bg-yellow-50'
                : 'bg-red-50'
          }
        />
        <StatCard
          label="Burn Rate"
          value={fmtTokens(health.burnRate)}
          subtext="tokens/24h"
          colorClass="text-purple-600"
          bgClass="bg-purple-50"
        />
        <StatCard
          label="Active Alerts"
          value={String(alertCount)}
          subtext={unacknowledgedCount > 0 ? `${unacknowledgedCount} unread` : 'all clear'}
          colorClass={alertCount > 0 ? 'text-red-600' : 'text-green-600'}
          bgClass={alertCount > 0 ? 'bg-red-50' : 'bg-green-50'}
          showBadge={alertCount > 0}
        />
      </div>

      {/* Budget Progress Bar */}
      <Card title="Budget Progress" subtitle={`${fmtNum(health.totalSpent)} of ${fmtNum(health.totalBudget)} tokens consumed`} className="mb-6">
        <div className="space-y-3">
          <div className="relative h-6 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ${getProgressColor(spentPct)}`}
              style={{ width: `${spentPct}%` }}
            >
              {spentPct > 12 && (
                <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-semibold">
                  {spentPct.toFixed(1)}%
                </span>
              )}
            </div>
            {spentPct <= 12 && (
              <span className="absolute inset-0 flex items-center justify-center text-gray-500 text-xs font-semibold">
                {spentPct.toFixed(1)}%
              </span>
            )}
          </div>

          {/* Threshold markers */}
          <div className="flex items-center justify-between text-[10px] text-gray-400 px-1">
            <span>0%</span>
            <span className="text-green-500 font-medium">70% safe</span>
            <span className="text-yellow-500 font-medium">85% caution</span>
            <span>100%</span>
          </div>
        </div>
      </Card>

      {/* Active Alerts Section */}
      <Card
        title="Active Alerts"
        subtitle={`${filteredAlerts.length} alert${filteredAlerts.length !== 1 ? 's' : ''}`}
        className="mb-6"
        action={
          <div className="flex items-center gap-1">
            {(['all', 'unacknowledged'] as AlertFilter[]).map((f) => (
              <button
                key={f}
                onClick={() => setAlertFilter(f)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                  alertFilter === f
                    ? 'bg-ldaja-blue text-white'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                {f === 'all' ? `All (${alerts.length})` : `Unread (${unacknowledgedCount})`}
              </button>
            ))}
          </div>
        }
      >
        {filteredAlerts.length === 0 ? (
          <div className="text-center py-8">
            <div className="mx-auto w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-700">No active alerts</p>
            <p className="text-xs text-gray-400 mt-1">Budget is on track</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredAlerts.map((alert) => (
              <AlertRow
                key={alert.id}
                alert={alert}
                isAcknowledging={acknowledgingId === alert.id}
                onAcknowledge={() => handleAcknowledge(alert.id)}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Intervention Log (Collapsible) */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <button
          onClick={() => setShowInterventions(!showInterventions)}
          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
        >
          <div>
            <h3 className="text-sm font-semibold text-ldaja-black">Intervention Log</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {interventions.length} past intervention{interventions.length !== 1 ? 's' : ''}
            </p>
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${showInterventions ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {showInterventions && (
          <div className="px-4 pb-4 border-t border-gray-100">
            {interventions.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center">No interventions recorded yet.</p>
            ) : (
              <div className="space-y-3 mt-3">
                {interventions.map((intervention) => (
                  <InterventionRow key={intervention.id} intervention={intervention} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Stat Card ────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  subtext,
  colorClass,
  bgClass,
  showBadge,
}: {
  label: string;
  value: string;
  subtext: string;
  colorClass: string;
  bgClass: string;
  showBadge?: boolean;
}) {
  return (
    <div className={`${bgClass} rounded-lg p-4 relative`}>
      {showBadge && (
        <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
      )}
      <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{label}</p>
      <p className={`text-2xl font-bold ${colorClass} mt-1`}>{value}</p>
      <p className="text-xs text-gray-400 mt-0.5">{subtext}</p>
    </div>
  );
}

// ── Alert Row ────────────────────────────────────────────────────────

function AlertRow({
  alert,
  isAcknowledging,
  onAcknowledge,
}: {
  alert: BurnAlert;
  isAcknowledging: boolean;
  onAcknowledge: () => void;
}) {
  const severity = severityConfig[alert.severity] || severityConfig.info;

  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
        alert.acknowledged
          ? 'bg-gray-50 border-gray-100 opacity-60'
          : 'bg-white border-gray-200'
      }`}
    >
      {/* Severity icon */}
      <div className="shrink-0 mt-0.5">
        {alert.severity === 'critical' ? (
          <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        ) : alert.severity === 'warning' ? (
          <svg className="w-5 h-5 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        ) : (
          <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
          </svg>
        )}
      </div>

      {/* Alert content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Badge variant={severity.variant}>{severity.label}</Badge>
          <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-medium">
            {alertTypeLabels[alert.type] || alert.type}
          </span>
        </div>
        <p className="text-sm text-gray-700">{alert.message}</p>
        <p className="text-xs text-gray-400 mt-1">
          {fmtDateTime(alert.createdAt)}
          {alert.acknowledged && alert.acknowledgedAt && (
            <span> &middot; Acknowledged {fmtDateTime(alert.acknowledgedAt)}</span>
          )}
        </p>
      </div>

      {/* Acknowledge button */}
      {!alert.acknowledged && (
        <Button
          variant="secondary"
          size="sm"
          isLoading={isAcknowledging}
          onClick={onAcknowledge}
          className="shrink-0"
        >
          Acknowledge
        </Button>
      )}
      {alert.acknowledged && (
        <span className="shrink-0 text-xs text-gray-400 mt-1">
          <svg className="w-4 h-4 inline text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M5 13l4 4L19 7" />
          </svg>
        </span>
      )}
    </div>
  );
}

// ── Intervention Row ─────────────────────────────────────────────────

function InterventionRow({ intervention }: { intervention: BurnIntervention }) {
  return (
    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
      <div className="shrink-0 mt-0.5">
        <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path d="M11.42 15.17l-4.655-5.653a2.548 2.548 0 010-3.586 2.548 2.548 0 013.586 0l1.06 1.06 1.06-1.06a2.548 2.548 0 013.586 0 2.548 2.548 0 010 3.586L11.42 15.17z" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-medium px-2 py-0.5 rounded bg-purple-50 text-purple-700">
            {interventionTypeLabels[intervention.type] || intervention.type}
          </span>
          <span className="text-xs text-gray-400">by {intervention.initiatedBy}</span>
        </div>
        <p className="text-sm text-gray-700">{intervention.description}</p>
        <p className="text-xs text-gray-400 mt-1">{fmtDateTime(intervention.createdAt)}</p>
      </div>
    </div>
  );
}
